import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { getDatabase, ref, push, set } from 'firebase/database';
import { Plus, X, Upload, FileText } from 'lucide-react';
import { OperatingCost, CostInterval, IntervalUnit } from '../../types/operatingCosts';
import { FileAttachment } from '../../types/attachments';
import { useOperatingCostCategories } from '../../contexts/OperatingCostCategoriesContext';
import { formatFileSize } from '../../Helpers/OperatingCostAttachmentUtils';
import { useDragDrop } from '../../hooks/useDragDrop';
import { UploadPickerModal } from '../../Components/UploadPickerModal';
import { recordActivity } from '../../api/activityApi';

interface OperatingCostModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (data: {
        categoryKey: string;
        amount: number;
        interval: CostInterval;
        description?: string;
        supplier?: string;
        filesToUpload?: File[];
        attachmentsToRemove?: string[];
    }) => Promise<void>;
    cost?: OperatingCost; // If provided, we're editing
    submitting?: boolean;
}

// Common interval presets
const INTERVAL_PRESETS = [
    { label: 'Månadsvis', count: 1, unit: 'month' as IntervalUnit },
    { label: 'Kvartalsvis', count: 3, unit: 'month' as IntervalUnit },
    { label: 'Halvårsvis', count: 6, unit: 'month' as IntervalUnit },
    { label: 'Årsvis', count: 1, unit: 'year' as IntervalUnit },
    { label: 'Vartannat år', count: 2, unit: 'year' as IntervalUnit },
    { label: 'Anpassad', count: 0, unit: 'month' as IntervalUnit },
];

const OperatingCostModal: React.FC<OperatingCostModalProps> = ({
    show,
    onHide,
    onSubmit,
    cost,
    submitting = false
}) => {
    const { categories, refresh: refreshCategories } = useOperatingCostCategories();
    const isEditing = !!cost;

    // Form state
    const [categoryKey, setCategoryKey] = useState('');
    const [amount, setAmount] = useState('');
    const [intervalPreset, setIntervalPreset] = useState('');
    const [customIntervalCount, setCustomIntervalCount] = useState('1');
    const [customIntervalUnit, setCustomIntervalUnit] = useState<IntervalUnit>('month');
    const [description, setDescription] = useState('');
    const [supplier, setSupplier] = useState('');
    const [error, setError] = useState<string | null>(null);

    // File attachment state
    const [existingAttachments, setExistingAttachments] = useState<FileAttachment[]>([]);
    const [attachmentsToRemove, setAttachmentsToRemove] = useState<string[]>([]);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);

    // Handle selected/dropped files with validation
    const handleFiles = useCallback((files: File[]) => {
        const allowedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif'];
        const allowedMimeTypes = [
            'application/pdf', 'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'image/jpeg', 'image/png', 'image/gif'
        ];
        const validFiles = files.filter(file => {
            const ext = '.' + file.name.split('.').pop()?.toLowerCase();
            return allowedMimeTypes.includes(file.type) || allowedExtensions.includes(ext);
        });
        if (validFiles.length === 0 && files.length > 0) {
            setError('Endast PDF, Word, Excel eller bilder är tillåtna');
            return;
        }
        if (validFiles.length > 0) {
            setPendingFiles(prev => [...prev, ...validFiles]);
        }
    }, []);

    const { isDragOver, dragHandlers } = useDragDrop({ onFilesDropped: handleFiles });

    // Upload picker modal state
    const [showUploadModal, setShowUploadModal] = useState(false);

    // New category modal state
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [savingCategory, setSavingCategory] = useState(false);
    const [categoryError, setCategoryError] = useState<string | null>(null);

    // Find preset matching an interval
    const findMatchingPreset = (interval: CostInterval): string => {
        const preset = INTERVAL_PRESETS.find(
            p => p.count === interval.count && p.unit === interval.unit
        );
        return preset?.label || 'Anpassad';
    };

    // Initialize form with cost data or defaults
    useEffect(() => {
        if (cost) {
            setCategoryKey(cost.categoryKey);
            setAmount(cost.amount.toString());
            const preset = findMatchingPreset(cost.interval);
            setIntervalPreset(preset);
            if (preset === 'Anpassad') {
                setCustomIntervalCount(cost.interval.count.toString());
                setCustomIntervalUnit(cost.interval.unit);
            }
            setDescription(cost.description || '');
            setSupplier(cost.supplier || '');
            // Load existing attachments
            const attachments = cost.attachments
                ? Object.values(cost.attachments)
                : [];
            setExistingAttachments(attachments);
        } else {
            setCategoryKey('');
            setAmount('');
            setIntervalPreset('Månadsvis');
            setCustomIntervalCount('1');
            setCustomIntervalUnit('month');
            setDescription('');
            setSupplier('');
            setExistingAttachments([]);
        }
        setError(null);
        setAttachmentsToRemove([]);
        setPendingFiles([]);
    }, [cost, show]);

    // Handle creating a new category
    const handleCreateCategory = async () => {
        const label = newCategoryName.trim();
        if (!label) return;

        setSavingCategory(true);
        setCategoryError(null);
        try {
            const db = getDatabase();
            const newRef = push(ref(db, 'config/operatingCostCategories'));
            await set(newRef, { label });
            recordActivity('settings.updated', { targetKey: newRef.key ?? undefined, label });

            // Refresh categories and select the new one
            await refreshCategories();
            setCategoryKey(newRef.key!);

            // Close the category modal
            setShowCategoryModal(false);
            setNewCategoryName('');
        } catch (err) {
            console.error('Error creating category:', err);
            setCategoryError('Kunde inte skapa kategorin');
        } finally {
            setSavingCategory(false);
        }
    };

    // Remove a pending file
    const removePendingFile = (index: number) => {
        setPendingFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Mark an existing attachment for removal
    const markAttachmentForRemoval = (key: string) => {
        setAttachmentsToRemove(prev => [...prev, key]);
    };

    // Undo removal of an existing attachment
    const undoAttachmentRemoval = (key: string) => {
        setAttachmentsToRemove(prev => prev.filter(k => k !== key));
    };

    // Get interval from form state
    const getInterval = (): CostInterval => {
        if (intervalPreset === 'Anpassad') {
            return {
                count: parseInt(customIntervalCount, 10) || 1,
                unit: customIntervalUnit
            };
        }
        const preset = INTERVAL_PRESETS.find(p => p.label === intervalPreset);
        return preset ? { count: preset.count, unit: preset.unit } : { count: 1, unit: 'month' };
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validation
        if (!categoryKey) {
            setError('Välj en kategori');
            return;
        }

        const parsedAmount = parseFloat(amount);
        if (isNaN(parsedAmount) || parsedAmount < 0) {
            setError('Ange ett giltigt belopp');
            return;
        }

        const interval = getInterval();
        if (interval.count < 1) {
            setError('Intervall måste vara minst 1');
            return;
        }

        const data = {
            categoryKey,
            amount: parsedAmount,
            interval,
            description: description.trim() || undefined,
            supplier: supplier.trim() || undefined,
            filesToUpload: pendingFiles.length > 0 ? pendingFiles : undefined,
            attachmentsToRemove: attachmentsToRemove.length > 0 ? attachmentsToRemove : undefined,
        };

        try {
            await onSubmit(data);
        } catch (err: any) {
            setError(err.message || 'Ett fel uppstod');
        }
    };

    // Attachments that are not marked for removal
    const visibleAttachments = existingAttachments.filter(
        a => !attachmentsToRemove.includes(a.key)
    );

    // Attachments marked for removal (to show with strikethrough)
    const removedAttachments = existingAttachments.filter(
        a => attachmentsToRemove.includes(a.key)
    );

    return (
        <>
            <Modal show={show} onHide={onHide} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isEditing ? 'Redigera driftkostnad' : 'Ny driftkostnad'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        {/* Category */}
                        <Form.Group className="mb-3">
                            <Form.Label>Kategori *</Form.Label>
                            <InputGroup>
                                <Form.Select
                                    value={categoryKey}
                                    onChange={(e) => setCategoryKey(e.target.value)}
                                    required
                                >
                                    <option value="">Välj kategori...</option>
                                    {categories.map(cat => (
                                        <option key={cat.key} value={cat.key}>
                                            {cat.label}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => {
                                        setNewCategoryName('');
                                        setCategoryError(null);
                                        setShowCategoryModal(true);
                                    }}
                                    title="Lägg till ny kategori"
                                >
                                    <Plus size={16} />
                                </Button>
                            </InputGroup>
                            {categories.length === 0 && (
                                <Form.Text className="text-warning">
                                    Inga kategorier skapade. Klicka på + för att lägga till en kategori.
                                </Form.Text>
                            )}
                        </Form.Group>

                        <div className="row">
                            {/* Amount */}
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Belopp (SEK) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="any"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        placeholder="0"
                                        required
                                    />
                                </Form.Group>
                            </div>

                            {/* Interval */}
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Betalningsintervall *</Form.Label>
                                    <Form.Select
                                        value={intervalPreset}
                                        onChange={(e) => setIntervalPreset(e.target.value)}
                                    >
                                        {INTERVAL_PRESETS.map(preset => (
                                            <option key={preset.label} value={preset.label}>
                                                {preset.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </div>
                        </div>

                        {/* Custom interval */}
                        {intervalPreset === 'Anpassad' && (
                            <div className="row">
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Var</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            value={customIntervalCount}
                                            onChange={(e) => setCustomIntervalCount(e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Enhet</Form.Label>
                                        <Form.Select
                                            value={customIntervalUnit}
                                            onChange={(e) => setCustomIntervalUnit(e.target.value as IntervalUnit)}
                                        >
                                            <option value="month">månad(er)</option>
                                            <option value="year">år</option>
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                            </div>
                        )}

                        {/* Supplier */}
                        <Form.Group className="mb-3">
                            <Form.Label>Leverantör</Form.Label>
                            <Form.Control
                                type="text"
                                value={supplier}
                                onChange={(e) => setSupplier(e.target.value)}
                                placeholder="t.ex. Vattenfall"
                                maxLength={200}
                            />
                            <small className={`d-block mt-1 text-end ${supplier.length >= 200 ? 'text-danger' : 'text-muted'}`}>
                                {supplier.length} / 200
                            </small>
                        </Form.Group>

                        {/* Description */}
                        <Form.Group className="mb-3">
                            <Form.Label>Beskrivning</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="t.ex. Fastprisavtal 3 år"
                                maxLength={500}
                            />
                            <small className={`d-block mt-1 text-end ${description.length >= 500 ? 'text-danger' : 'text-muted'}`}>
                                {description.length} / 500
                            </small>
                        </Form.Group>

                        {/* File Attachments */}
                        <div
                            {...dragHandlers}
                            style={{
                                padding: '12px',
                                marginBottom: '1rem',
                                borderRadius: '8px',
                                border: isDragOver ? '2px dashed #007bff' : '2px dashed #dee2e6',
                                backgroundColor: isDragOver ? '#f0f8ff' : 'transparent',
                                transition: 'all 0.2s ease'
                            }}
                        >
                        <Form.Group>
                            <Form.Label className="d-block">Bilagor</Form.Label>

                            {/* Existing attachments */}
                            {visibleAttachments.length > 0 && (
                                <div className="mb-2">
                                    {visibleAttachments.map(attachment => (
                                        <div
                                            key={attachment.key}
                                            className="d-flex align-items-center gap-2 p-2 bg-light rounded mb-1"
                                        >
                                            <FileText size={16} className="text-muted" />
                                            <span className="flex-grow-1 text-truncate" style={{ fontSize: '14px' }}>
                                                {attachment.fileName}
                                            </span>
                                            <span className="text-muted" style={{ fontSize: '12px' }}>
                                                {formatFileSize(attachment.fileSize)}
                                            </span>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 text-danger"
                                                onClick={() => markAttachmentForRemoval(attachment.key)}
                                                title="Ta bort"
                                            >
                                                <X size={16} />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Attachments marked for removal */}
                            {removedAttachments.length > 0 && (
                                <div className="mb-2">
                                    {removedAttachments.map(attachment => (
                                        <div
                                            key={attachment.key}
                                            className="d-flex align-items-center gap-2 p-2 bg-light rounded mb-1 opacity-50"
                                        >
                                            <FileText size={16} className="text-muted" />
                                            <span
                                                className="flex-grow-1 text-truncate text-decoration-line-through"
                                                style={{ fontSize: '14px' }}
                                            >
                                                {attachment.fileName}
                                            </span>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 text-primary"
                                                onClick={() => undoAttachmentRemoval(attachment.key)}
                                                title="Ångra borttagning"
                                            >
                                                Ångra
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Pending files to upload */}
                            {pendingFiles.length > 0 && (
                                <div className="mb-2">
                                    {pendingFiles.map((file, index) => (
                                        <div
                                            key={`pending-${index}`}
                                            className="d-flex align-items-center gap-2 p-2 bg-success bg-opacity-10 rounded mb-1"
                                        >
                                            <Upload size={16} className="text-success" />
                                            <span className="flex-grow-1 text-truncate" style={{ fontSize: '14px' }}>
                                                {file.name}
                                            </span>
                                            <span className="text-muted" style={{ fontSize: '12px' }}>
                                                {formatFileSize(file.size)}
                                            </span>
                                            <span className="badge bg-success" style={{ fontSize: '10px' }}>
                                                Ny
                                            </span>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 text-danger"
                                                onClick={() => removePendingFile(index)}
                                                title="Ta bort"
                                            >
                                                <X size={16} />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Upload button */}
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setShowUploadModal(true)}
                                disabled={submitting}
                            >
                                <Upload size={14} className="me-1" />
                                Lägg till fil
                            </Button>
                            <Form.Text className="text-muted d-block mt-1">
                                PDF, Word, Excel eller bilder. Du kan också dra och släppa filer här.
                            </Form.Text>
                        </Form.Group>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={onHide} disabled={submitting}>
                            Avbryt
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    {isEditing ? 'Sparar...' : 'Lägger till...'}
                                </>
                            ) : (
                                isEditing ? 'Spara' : 'Lägg till'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* New Category Modal */}
            <Modal
                show={showCategoryModal}
                onHide={() => setShowCategoryModal(false)}
                centered
                size="sm"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Ny kategori</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {categoryError && (
                        <Alert variant="danger" dismissible onClose={() => setCategoryError(null)}>
                            {categoryError}
                        </Alert>
                    )}
                    <Form.Group>
                        <Form.Label>Namn *</Form.Label>
                        <Form.Control
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            placeholder="t.ex. Elektricitet"
                            autoFocus
                            disabled={savingCategory}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCreateCategory();
                                }
                            }}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowCategoryModal(false)}
                        disabled={savingCategory}
                    >
                        Avbryt
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleCreateCategory}
                        disabled={!newCategoryName.trim() || savingCategory}
                    >
                        {savingCategory ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Sparar...
                            </>
                        ) : (
                            'Lägg till'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Upload picker (drag-drop, paste, or pick from device) */}
            <UploadPickerModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                onFilesSelected={handleFiles}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                multiple
                title="Ladda upp"
            />
        </>
    );
};

export default OperatingCostModal;
